import React from 'react';
import type { SiteRagReport } from '../../../../types/siteRag';
import type { SiteEntity } from '../../../../types/site';
import { SectionShell } from '../SectionShell';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { EditorialDefinitionList, EditorialSubsection } from '../../report/editorial';
import { buildAdaptiveProjectIntentOverviewModel } from '../../../../lib/projectIntentOverviewModel';

interface Props {
  report: SiteRagReport;
  label: string;
  reportSite?: SiteEntity;
}

/**
 * W2d editorial port — the two uppercase-tracking `<h3>` prose blocks
 * (Site summary / Brief objective) become EditorialSubsection (hairline
 * rule + small-caps mono label); the counterparties / key-dates footer
 * strip becomes an EditorialDefinitionList ledger (label/value shape).
 * Authored strings are unchanged and citation walking is preserved;
 * empty / whitespace-only inputs drop their block (the ledger renders
 * only the rows that carry real values).
 *
 * Mounted via SectionsRouter (`projectContext`); export name + props
 * signature are load-bearing for the registry — do not change them.
 */
export const CustomBriefProjectContextSection: React.FC<Props> = ({ report, label, reportSite }) => {
  const theme = useEffectiveReportTheme();
  const ctx = report.customBriefProjectContext;
  const adaptiveModel = buildAdaptiveProjectIntentOverviewModel(report, reportSite);
  const hasAdaptiveData =
    adaptiveModel.showModeScorecard ||
    adaptiveModel.intentDrift.hasInputDrift ||
    adaptiveModel.preservedUserDecisions.length > 0;
  const hasData = Boolean(
    ctx?.siteSummary?.trim() || ctx?.intentRestatement?.trim() || hasAdaptiveData,
  );

  const ledgerItems = [
    ...(report.customBriefCounterparties?.trim()
      ? [{ label: 'Counterparties', value: report.customBriefCounterparties }]
      : []),
    ...(report.customBriefKeyDates?.trim()
      ? [{ label: 'Key dates', value: report.customBriefKeyDates }]
      : []),
  ];

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {!ctx && !hasAdaptiveData ? null : (
        <article className="space-y-4">
          {ctx?.siteSummary?.trim() && (
            <EditorialSubsection theme={theme} label="Site summary">
              <p className="leading-relaxed">
                {walkCitationText(ctx.siteSummary, report.references)}
              </p>
            </EditorialSubsection>
          )}
          {ctx?.intentRestatement?.trim() && (
            <EditorialSubsection theme={theme} label="Brief objective">
              <p className="leading-relaxed">
                {walkCitationText(ctx.intentRestatement, report.references)}
              </p>
            </EditorialSubsection>
          )}
          {hasAdaptiveData && (
            <div
              className="space-y-3"
              data-testid="custom-brief-adaptive-overview"
              data-screening-mode={adaptiveModel.screeningMode}
              data-recommendation-status={adaptiveModel.recommendationStatus.status}
            >
              {adaptiveModel.intentDrift.hasInputDrift && (
                <div data-testid="custom-brief-stale-intent-warning" className="text-xs leading-relaxed">
                  <strong>Stale Project Intent warning:</strong> {adaptiveModel.intentDrift.summaryText}
                </div>
              )}
              {adaptiveModel.screeningMode === 'recommend' ? (
                <EditorialSubsection theme={theme} label="Saved screening recommendation (Recommend an approach)">
                  <div className="space-y-2 text-xs leading-relaxed">
                    <p className="m-0 font-semibold">
                      {adaptiveModel.recommendationStatus.label} — {adaptiveModel.recommendationStatus.explanation}
                    </p>
                    <dl className="m-0 space-y-1.5">
                      {[adaptiveModel.powerRecommendation, ...adaptiveModel.supportingRecommendations].map((row) => (
                        <div key={row.id}>
                          <dt className="font-semibold inline">{row.label} ({row.statusLabel}): </dt>
                          <dd className="inline m-0">{walkCitationText(row.value, report.references)}</dd>
                          {row.notes.length > 0 && (
                            <div className="text-[11px] opacity-85">
                              {row.notes.map((n, idx) => (
                                <span key={idx}>{idx > 0 ? ' · ' : ''}{walkCitationText(n, report.references)}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </dl>
                    {adaptiveModel.alternativesList.length > 0 && (
                      <div>
                        <strong>Screened alternatives: </strong>
                        {adaptiveModel.alternativesList.map((alt, idx) => (
                          <span key={idx}>{idx > 0 ? '; ' : ''}{walkCitationText(alt, report.references)}</span>
                        ))}
                      </div>
                    )}
                    {adaptiveModel.evidenceGaps.length > 0 && (
                      <div className="space-y-1">
                        <strong>Material evidence gaps &amp; next steps:</strong>
                        <ul className="list-disc pl-4 m-0 space-y-0.5">
                          {adaptiveModel.evidenceGaps.map((g) => (
                            <li key={g.id}>
                              <strong>{g.label}:</strong> {walkCitationText(g.gap, report.references)}
                              {g.nextStep ? (
                                <span> — <em>Next step:</em> {walkCitationText(g.nextStep, report.references)}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {adaptiveModel.recordedBriefRows.length > 0 && (
                      <div>
                        <strong>Supplied project constraints (not screening recommendations): </strong>
                        {adaptiveModel.recordedBriefRows.map((r, idx) => (
                          <span key={r.label}>
                            {idx > 0 ? ' · ' : ''}
                            {r.label}: {String(r.value)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </EditorialSubsection>
              ) : (
                <EditorialSubsection theme={theme} label="Proposal evaluation vs. screening evidence (Evaluate my proposal)">
                  <div className="space-y-2 text-xs leading-relaxed">
                    <p className="m-0 font-medium">{adaptiveModel.proposalSummaryLead}</p>
                    <ul className="list-none pl-0 m-0 space-y-1.5">
                      {adaptiveModel.proposalRows.map((row) => (
                        <li key={row.dimension} data-status={row.assessmentStatus}>
                          <strong>{row.dimension} [{row.badge.label}]:</strong> Proposal: {row.declaredProposal} — Evidence:{' '}
                          {walkCitationText(row.verifiedEvidence, report.references)} — {row.gapAndNextStep}
                        </li>
                      ))}
                    </ul>
                    {adaptiveModel.proposalAlternative && (
                      <div>
                        <strong>{adaptiveModel.proposalAlternative.label}: </strong>
                        {walkCitationText(adaptiveModel.proposalAlternative.summary, report.references)}
                        {adaptiveModel.proposalAlternative.rationale ? (
                          <span> ({walkCitationText(adaptiveModel.proposalAlternative.rationale, report.references)})</span>
                        ) : null}
                      </div>
                    )}
                  </div>
                </EditorialSubsection>
              )}
              {adaptiveModel.preservedUserDecisions.length > 0 && (
                <div className="text-xs leading-relaxed">
                  <strong>Preserved user corrections: </strong>
                  {adaptiveModel.preservedUserDecisions.join(' · ')}
                </div>
              )}
            </div>
          )}
          {ledgerItems.length > 0 && (
            // Carrier div holds the testid (EditorialDefinitionList has no testid prop).
            <div data-testid="custom-brief-context-ledger">
              <EditorialDefinitionList theme={theme} items={ledgerItems} />
            </div>
          )}
        </article>
      )}
    </SectionShell>
  );
};
